import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
	getAllItApplications,
	createItApplicationItProcessRelationship,
	deleteItappItprocessRelation
} from '../../../../../../actions/ITApplication/itapplicationaction';
import {getAllItProcesses} from '../../../../../../actions/ITProcess/itprocessactions';
import ISA315RelateItProcessItApplications from './ISA315RelateItProcessItApplications';
import Utility from '../../../../../../util/utilityfunctions';
import {labels, GetCardTypeForBody} from '../../../../../../util/utils';
import {setItApplicationSelected, setItProcessSelected} from '../../../../../../actions/BodySpecial/bodySpecialActions';
import {entities, itApplicationFilter, clientSideEventNames, Entity} from '../../../../../../util/uiconstants';
import BodyKnowledgeDescription from '../../../../CanvasForm/Bodies/BodyKnowledgeDescription';
import {useDocumentId, useEngagementId} from '../../../../../../util/customHooks';
import CreateEditITApplicationModalWrapper from '../../../../EntityCrud/CreateEditITApplicationModalWrapper';
import CreateEditITProcessModalWrapper from '../../../../EntityCrud/CreateEditITProcessModalWrapper';
import BodySpecialDeleteItemModal from './BodySpecialDeleteItemModal';
import {deleteItProcess} from '../../../../../../actions/ITProcess/itprocessactions';
import {deleteItApplication} from '../../../../../../actions/ITApplication/itapplicationaction';
import {clientSideSearch} from '../../../../../../actions/BodySpecial/bodySpecialActions';

function ISA315RelateItProcessItApp({
	className,
	cardTypeId,
	body,
	bodyDescriptionText,
	bodyId,
	evidenceDocumentReferenceCount,
	priorPeriodDocumentReferenceCount,
	getBodiesBySectionId,
	guidanceFilter,
	headerId,
	isAresView,
	isDuplicate,
	isSnapshotView,
	sectionId,
	showGuidance,
	showRelateDocumentsIcon,
	updateBodyOptions
}) {
	const engagementId = useEngagementId(),
		documentId = useDocumentId();

	const config = useMemo(
		() => ({
			itApplications: {
				callback: (engagementId, filters) =>
					getAllItApplications(
						engagementId,
						{
							bodyId: `${bodyId}_itApplications`,
							entityId: entities.ItSoApplication
						},
						{
							...filters,
							filterItApplicationType: itApplicationFilter.itApplication,
							includeITProcess: true
						}
					),
				selector: (state, bodyId) => {
					let allIds = state.bodiesSpecial.metadata[`${bodyId}_itApplications`]?.allIds;
					if (allIds && allIds.length > 0) {
						let itApplications = allIds.map((id) => state.bodiesSpecial.itApplications?.byId[id]);
						Utility.sortAlphaNumeric(itApplications, 'itApplicationName');
						return itApplications.map((itApplication) => itApplication.id);
					}
				},
				filterSelector: (state, bodyId) => {
					let filteredIds = state.bodiesSpecial.metadata[`${bodyId}_itApplications`]?.filteredIds;
					if (filteredIds && filteredIds.length > 0) {
						let itApplications = filteredIds.map((id) => state.bodiesSpecial.itApplications?.byId[id]);
						Utility.sortAlphaNumeric(itApplications, 'itApplicationName');
						return itApplications.map((itApplication) => itApplication.id);
					}
				},
				byId: (state, id) => state.bodiesSpecial.itApplications?.byId[id],
				entityColor: 'green600',
				entityConfig: (entity) => [
					{
						name: 'edit',
						render: <div>{labels.editItApp}</div>,
						onClick: () => {
							onEditITApplication({
								entityId: entities.ItSoApplication,
								entityUId: entity.id,
								entityName: entity.itApplicationName
							});
						}
					},
					{
						name: 'delete',
						render: <div>{labels.removeItAppOption}</div>,
						onClick: () => {
							onDeleteItApplication({
								entityId: entities.ItSoApplication,
								entityUId: entity.id,
								entityName: entity.itApplicationName
							});
						}
					}
				],
				entityCount: (entity) => entity.itProcesses?.length,
				entityName: (entity) => entity.itApplicationName,
				entityProps: (entity) => ({
					allIds: entity.itProcesses,
					nameKey: 'itProcessName',
					childEntityId: Entity.ITProcess,
					selector: (state, id) => state.bodiesSpecial.itProcesses.byId[id],
					title: labels.itProcessesLabel,
					showAllLabel: labels.showAllItProcesses,
					header: Utility.createRichTextElement(
						labels.relatedToLabel
							.replace('{count}', entity.itProcesses?.length)
							.replace('{child}', labels.itProcessesLabel)
							.replace('{parent}', entity.itApplicationName),
						'display-all-items-modal-header'
					)
				}),
				relateEntityAction: createItApplicationItProcessRelationship,
				header: labels.itProcessesHeaderRelate,
				searchPlaceholder: labels.searchItApplication,
				noDataPlaceholder: {
					text: labels.itAppNoDataPlaceHolder,
					config: {
						identifyItApp: () => onEditITApplication({entityUId: null})
					}
				},
				onRemove: (fatherId, id, loadingSetter) => {
					dispatch(deleteItappItprocessRelation(engagementId, 'itApplication', 'itProcesses', id, fatherId)).finally(
						() => {
							loadingSetter(false);
							window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
						}
					);
				}
			},
			itProcesses: {
				callback: (engagementId, filters) =>
					getAllItProcesses(
						engagementId,
						{
							bodyId: `${bodyId}_itProcesses`,
							entityId: entities.ItProcess
						},
						{
							...filters,
							includeITApp: true,
							filterItApplicationType: itApplicationFilter.itApplication
						}
					),
				selector: (state, bodyId) => {
					let allIds = state.bodiesSpecial.metadata[`${bodyId}_itProcesses`]?.allIds;
					if (allIds && allIds.length > 0) {
						let itProcesses = allIds.map((id) => state.bodiesSpecial.itProcesses?.byId[id]);
						Utility.sortAlphaNumeric(itProcesses, 'itProcessName');
						return itProcesses.map((itProcess) => itProcess.id);
					}
				},
				filterSelector: (state, bodyId) => {
					let filteredIds = state.bodiesSpecial.metadata[`${bodyId}_itProcesses`]?.filteredIds;
					if (filteredIds && filteredIds.length > 0) {
						let itProcesses = filteredIds.map((id) => state.bodiesSpecial.itProcesses?.byId[id]);
						Utility.sortAlphaNumeric(itProcesses, 'itProcessName');
						return itProcesses.map((itProcess) => itProcess.id);
					}
				},
				byId: (state, id) => state.bodiesSpecial.itProcesses?.byId[id],
				entityColor: 'green600',
				entityConfig: (entity) => [
					{
						name: 'edit',
						render: <div>{labels.editItProcess}</div>,
						onClick: () => {
							onEditITProcess({
								entityId: entities.ItProcess,
								entityUId: entity.id,
								entityName: entity.itProcessName
							});
						}
					},
					{
						name: 'delete',
						render: <div>{labels.deleteItProcess}</div>,
						onClick: () => {
							onDeleteItProcess({
								entityId: entities.ItProcess,
								entityUId: entity.id,
								entityName: entity.itProcessName
							});
						}
					}
				],
				entityCount: (entity) => entity.itApplications?.length,
				entityName: (entity) => entity.itProcessName,
				entityProps: (entity) => ({
					allIds: entity.itApplications,
					nameKey: 'itApplicationName',
					childEntityId: Entity.ITSOApplication,
					selector: (state, id) => state.bodiesSpecial.itApplications.byId[id],
					title: labels.itApplicationsLabel,
					showAllLabel: labels.showAllItAplications,
					header: Utility.createRichTextElement(
						labels.relatedToLabel
							.replace('{count}', entity.itApplications?.length)
							.replace('{child}', labels.itApplicationsLabel)
							.replace('{parent}', entity.itProcessName),
						'display-all-items-modal-header'
					)
				}),
				header: labels.itApplicationHeaderRelate,
				searchPlaceholder: labels.searchItProcess,
				noDataPlaceholder: {
					text: labels.itProcessNoDataPlaceHolderRelation,
					config: {
						identifyItProcess: () => onEditITProcess({entityUId: null})
					}
				},
				onRemove: (fatherId, id, loadingSetter) => {
					dispatch(deleteItappItprocessRelation(engagementId, 'itApplication', 'itProcesses', fatherId, id)).finally(
						() => {
							loadingSetter(false);
							window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
						}
					);
				}
			}
		}),
		[bodyId]
	);

	const bodyModel = useMemo(
		() => ({
			id: bodyId,
			cardTypeId,
			bodyTypeId: body.bodyTypeId,
			engagementComments: body.engagementComments,
			evidenceDocumentReferenceCount: body.evidenceDocumentReferenceCount,
			priorPeriodDocumentReferenceCount: body.priorPeriodDocumentReferenceCount,
			guidances: body.guidances
		}),
		[bodyId, cardTypeId, body, evidenceDocumentReferenceCount, priorPeriodDocumentReferenceCount]
	);

	const dispatch = useDispatch();
	const handleOnItApplicationClick = useCallback((id) => dispatch(setItApplicationSelected(id)), [dispatch]);
	const handleOnItProcessClick = useCallback((id) => dispatch(setItProcessSelected(id)), [dispatch]);

	const [itApplicationId, setITApplicationId] = useState(null),
		[itApplicationName, setITApplicationName] = useState(null),
		[itProcessId, setITProcessId] = useState(null),
		[itProcessName, setITProcessName] = useState(null),
		[showITApplicationModal, setShowITApplicationModal] = useState(false),
		[showITProcessModal, setShowITProcessModal] = useState(false),
		[showDeleteITApplicationModal, setShowDeleteITApplicationModal] = useState(false),
		[showDeleteITProcessModal, setShowDeleteITProcessModal] = useState(false),
		[filterData, setFilterData] = useState(null);

	const onEditITApplication = useCallback(
		(clickArgs) => {
			setShowITApplicationModal(!showITApplicationModal);
			setITApplicationId(clickArgs.entityUId);
		},
		[showITApplicationModal]
	);

	const onEditITProcess = useCallback(
		(clickArgs) => {
			setShowITProcessModal(!showITProcessModal);
			setITProcessId(clickArgs.entityUId);
		},
		[showITProcessModal]
	);

	const onDeleteItApplication = useCallback(
		(clickArgs) => {
			setShowDeleteITApplicationModal(!showDeleteITApplicationModal);
			setITApplicationId(clickArgs.entityUId);
			setITApplicationName(clickArgs.entityName);
		},
		[showDeleteITProcessModal]
	);

	const onDeleteItProcess = useCallback(
		(clickArgs) => {
			setShowDeleteITProcessModal(!showDeleteITProcessModal);
			setITProcessId(clickArgs.entityUId);
			setITProcessName(clickArgs.entityName);
		},
		[showDeleteITProcessModal]
	);

	const handleOnFilter = useCallback((filterData) => {
		setFilterData(filterData);
	}, []);

	const handleOnSaveComplete = useCallback(() => {
		if (filterData?.filters) {
			dispatch(clientSideSearch(`${bodyId}_${filterData.entityName}`, filterData.entityName, filterData.filters));
		}
		window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
	}, [filterData]);

	return (
		<StyledISA315RelateItProcessItApp className={`bodyWrapper ${className} ${GetCardTypeForBody(body, isAresView)}`}>
			<section className={`bodyWrapperSection ${GetCardTypeForBody(body, isAresView)}`}>
				{body && (
					<BodyKnowledgeDescription
						isAresView={isAresView}
						showGuidance={showGuidance}
						guidanceFilter={guidanceFilter}
						isSnapshotView={isSnapshotView}
						engagementId={engagementId}
						documentId={documentId}
						headerId={headerId}
						bodyModel={bodyModel}
						sectionId={sectionId}
						bodyId={bodyId}
						bodyDescriptionText={bodyDescriptionText}
						isCustom={false}
						isDuplicate={isDuplicate}
						showRelateDocumentsIcon={showRelateDocumentsIcon}
						showDescription={!isAresView}
						getBodiesBySectionId={getBodiesBySectionId}
						updateBodyOptions={updateBodyOptions}
					/>
				)}
				{showITApplicationModal && (
					<CreateEditITApplicationModalWrapper
						isManageEntities
						isCreate={itApplicationId === null}
						isEdit={itApplicationId !== null}
						entityId={itApplicationId}
						showModal={showITApplicationModal}
						setShowModal={setShowITApplicationModal}
						onSaveComplete={handleOnSaveComplete}
					/>
				)}
				{showITProcessModal && (
					<CreateEditITProcessModalWrapper
						isManageEntities
						isCreate={itProcessId === null}
						isEdit={itProcessId !== null}
						entityId={itProcessId}
						showModal={showITProcessModal}
						setShowModal={setShowITProcessModal}
						onSaveComplete={handleOnSaveComplete}
					/>
				)}
				{showDeleteITProcessModal && (
					<BodySpecialDeleteItemModal
						title={labels.deleteItProcess}
						name={itProcessName}
						show={showDeleteITProcessModal}
						onHide={() => setShowDeleteITProcessModal(false)}
						onDelete={() => {
							dispatch(deleteItProcess(Number(engagementId), bodyId, itProcessId)).then(() => {
								window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
								setShowDeleteITProcessModal(false);
							});
						}}
						setIsLoading={() => {}}
					/>
				)}
				{showDeleteITApplicationModal && (
					<BodySpecialDeleteItemModal
						title={labels.removeItAppOption}
						name={itApplicationName}
						show={showDeleteITApplicationModal}
						onHide={() => setShowDeleteITApplicationModal(false)}
						onDelete={() => {
							dispatch(deleteItApplication(Number(engagementId), bodyId, itApplicationId)).then(() => {
								window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
								setShowDeleteITApplicationModal(false);
							});
						}}
						setIsLoading={() => {}}
					/>
				)}
				<section className="isa315-relate-itprocess-itapplications-body">
					<ISA315RelateItProcessItApplications
						{...config.itApplications}
						engagementId={engagementId}
						bodyId={bodyId}
						className="isa315-relate-itprocess-itapplications-body-left"
						entity={labels.itApplicationHeaderRelate}
						entityId={entities.ItSoApplication}
						onClick={handleOnItApplicationClick}
						onFilter={handleOnFilter}
						displayToolbar
					/>
					<ISA315RelateItProcessItApplications
						{...config.itProcesses}
						engagementId={engagementId}
						bodyId={bodyId}
						className="isa315-relate-itprocess-itapplications-body-right"
						dragAndDrop
						entity={labels.itProcessesHeaderRelate}
						entityId={entities.ItProcess}
						onClick={handleOnItProcessClick}
						onFilter={handleOnFilter}
						displayToolbar
					/>
				</section>
			</section>
		</StyledISA315RelateItProcessItApp>
	);
}

ISA315RelateItProcessItApp.propTypes = {
	className: PropTypes.string,
	bodyDescriptionText: PropTypes.string.isRequired,
	bodyId: PropTypes.number.isRequired,
	cardTypeId: PropTypes.number.isRequired,
	evidenceDocumentReferenceCount: PropTypes.number,
	priorPeriodDocumentReferenceCount: PropTypes.number,
	getBodiesBySectionId: PropTypes.func.isRequired,
	guidanceFilter: PropTypes.func,
	headerId: PropTypes.number.isRequired,
	isAresView: PropTypes.bool,
	isDuplicate: PropTypes.bool,
	isSnapshotView: PropTypes.bool,
	sectionId: PropTypes.number.isRequired,
	showGuidance: PropTypes.bool,
	showRelateDocumentsIcon: PropTypes.bool,
	updateBodyOptions: PropTypes.func
};

ISA315RelateItProcessItApp.defaultProps = {
	className: 'isa315-relate-itprocess-itapp',
	isAresView: false,
	isSnapshotView: false,
	showGuidance: true
};

export default ISA315RelateItProcessItApp;

const StyledISA315RelateItProcessItApp = styled.section`
	min-height: var(--px-300);
	max-height: var(--px-1140);
	display: flex;
	flex-direction: column;
	& > div {
		flex-grow: 1;
	}
	.bodyWrapperSection {
		display: flex;
		flex-direction: column;
		height: 100%;
		padding: var(--px-10) 0 var(--px-10) var(--px-10);
		.GuidanceWapper {
			margin-top: 0 !important;
			padding-left: 0 !important;
		}
	}
	.isa315-relate-itprocess-itapplications-body {
		display: flex;
		min-height: var(--px-300);
		margin: var(--px-20) 0 !important;
		max-height: var(--px-1020);
	}
	.title {
		border-bottom: var(--px-1) solid var(--neutrals-100);
	}
	.add-link {
		cursor: pointer;
		color: var(--blue-600);
		font-weight: bold;
	}
`;
